import AARTextField from "@/components/AARTextField";
import BaseDrawer from "@/components/BaseDrawer";
import { ASSIGNED, AVAILABLE } from "@/constants/area";
import DefaultPaginationConfig from "@/constants/pagination";
import { RELEASED, RELEASE_PENDING } from "@/constants/request";
import {
  ENGINEER,
  PROJECT_ADMIN,
  SUBCONTRACTOR,
  SUPER_ADMIN,
} from "@/constants/roles";
import { ACCEPTED, OVERDUE, REJECTED } from "@/constants/tasks";
import { useRequests } from "@/hooks/useRequests";
import { useSearchFilter } from "@/hooks/useSearchFilters";
import { useAllTasks } from "@/hooks/useTasks";
import { IArea, ILevel } from "@/interfaces/level.interface";
import { IRequest } from "@/interfaces/request.interface";
import { IRequestTasks, ITaskAction } from "@/interfaces/task.interface";
import { convertDateWithoutTimeString, isAuthorize } from "@/utils/common";
import { buildFilters } from "@/utils/kendo";
import { orderBy, SortDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import {
  Grid,
  GridColumn,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import {
  Info,
  ListCheck
} from "lucide-react";
import React, { useEffect, useState } from "react";
import "react-image-crop/dist/ReactCrop.css";
import ActivityLogs from "./ActivityLogs";

export const getPaginationData = () => {
  let Pagination: any = sessionStorage.getItem("Pagination");

  if (!Pagination) {
    Pagination = DefaultPaginationConfig;
  } else {
    Pagination = JSON.parse(Pagination);
  }

  return Pagination;
};

interface IProps {
  setIsViewMode: React.Dispatch<React.SetStateAction<boolean>>;
  readOnlyMode: boolean;
  handlePreview: any;
  showDetailsDrawer: boolean;
  setShowDetailsDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showAssignmentsDrawer: boolean;
  setShowAssignmentsDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showSubmissionDrawer: boolean;
  setShowSubmissionDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  selectedArea?: IArea;
  selectedRequest?: IRequest | null;
  setSelectedRequest: React.Dispatch<React.SetStateAction<IRequest | null>>;
  selectedLevel: ILevel | null;
  handleAction: (data: ITaskAction) => void;
  imagePreviewWidth?: string;
  imagePreviewHeight?: string;
}

function AssignmentsDrawer({
  setIsViewMode,
  readOnlyMode,
  showAssignmentsDrawer,
  handlePreview,
  setShowAssignmentsDrawer,
  setShowSubmissionDrawer,
  setShowDetailsDrawer,
  showDetailsDrawer,
  showSubmissionDrawer,
  selectedArea,
  selectedRequest,
  setSelectedRequest,
  selectedLevel,
  handleAction,
  imagePreviewWidth,
  imagePreviewHeight,
  ...drawerProps
}: IProps) {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (showAssignmentsDrawer) {
      setActiveTab(0);
    }
  }, [showAssignmentsDrawer]);

  const Pagination = getPaginationData();
  const { searchValue, dataState, handleSearchChange, setDataState } =
    useSearchFilter({
      initialState: Pagination.initialDataState,
      searchFields: ["name"],
    });

  const [page, setPage] = useState({ skip: 0, take: 10 });
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "createdOn", dir: "desc" },
  ]);

  const { data, isLoading, isRefetching: isRefetchingRequests } = useRequests(
    {
      pageNumber: page.skip,
      pageSize: page.take,
      sort: dataState.sort ?? null,
      filter: buildFilters(null, null, false, [
        {
          field: "areaId",
          operator: "eq",
          value: selectedArea?.id,
          logic: "and",
        },
        {
          field: "status",
          operator: "neq",
          value: "Released",
          logic: "and",
        },
      ]),
    },
    {
      enabled:
        showAssignmentsDrawer &&
        !!selectedArea &&
        !selectedArea?.id?.includes("new"),
      showLoader: false,
    }
  );

  // Get all request IDs to fetch their tasks
  const requestIds = data?.data?.map((r: IRequest) => r.id) || [];

  // Build requestId filters using "or" logic with multiple "eq" conditions
  const requestIdFilters = requestIds.map((id) => ({
    field: "requestId",
    operator: "eq",
    value: id,
  }));

  // Fetch tasks for all requests with IsActive: true
  const { data: tasksData, isLoading: isLoadingTasks, isRefetching: isRefetchingTasks } = useAllTasks(
    {
      filter: {
        logic: "and",
        filters: [
          {
            logic: "and",
            filters: [
              {
                field: "isActive",
                operator: "eq",
                value: true,
              },
            ],
          },
          ...(requestIdFilters.length > 0
            ? [
                {
                  logic: "or",
                  filters: requestIdFilters,
                },
              ]
            : []),
        ],
      } as any,
    },
    {
      enabled: showAssignmentsDrawer && requestIds.length > 0,
      showLoader: false,
    }
  );

  // Group tasks by requestId for easy lookup
  const tasksByRequestId = (tasksData?.data || []).reduce(
    (acc: Record<string, IRequestTasks[]>, task: IRequestTasks) => {
      if (!acc[task.requestId]) {
        acc[task.requestId] = [];
      }
      acc[task.requestId].push(task);
      return acc;
    },
    {}
  );

  // Check if a request has any rejected task
  const hasRejectedTask = (requestId: string): boolean => {
    const tasks = tasksByRequestId[requestId] || [];
    return tasks.some((task: IRequestTasks) => task.status === REJECTED);
  };

  const pageChange = (e: GridPageChangeEvent) => {
    setPage(e.page);
  };

  const sortChange = (e: GridSortChangeEvent) => {
    setSort(e.sort);
  };

  const gridData = data?.data || [];
  const total = data?.total || 0;

  // Combined loading state - show shimmer until both requests and tasks are loaded
  const isDataLoading = isLoading || isRefetchingRequests || (requestIds.length > 0 && (isLoadingTasks || isRefetchingTasks));

  // Shimmer placeholder data for loading state
  const shimmerData = Array.from({ length: 5 }, (_, i) => ({
    id: `shimmer-${i}`,
    refNo: '',
    from: '',
    to: '',
    status: '',
    companies: [],
  }));

  const onSelectRequest = (dataItem: any) => {
    setSelectedRequest(dataItem);
    setShowAssignmentsDrawer(false);
    setShowDetailsDrawer(true);
  };

  const ActionCell = (props: any) => {
    const { dataItem } = props;

    const canView =
      // PROJECT_ADMIN, SUPER_ADMIN and ENGINEER can always view:
      isAuthorize([PROJECT_ADMIN, SUPER_ADMIN, ENGINEER, SUBCONTRACTOR]); // ||
    // SUBCONTRACTOR can view only if one of the companies matches their userId:
    // (user?.roles.includes(SUBCONTRACTOR) &&
    //   dataItem.companies.some((c: any) => c.user === user.companyId));

    return (
      <td style={{padding: 0, border: 'none'}}>
        {canView && (
          <span className="inline-flex items-center gap-2">
            <Button
              className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
              fillMode="outline"
              onClick={() => onSelectRequest(dataItem)}
            >
              View
            </Button>
            {props?.dataItem?.status === ASSIGNED && hasRejectedTask(dataItem.id) && (
              <Info className="w-5 h-5 text-[#D32F2F]" />
            )}
          </span>
        )}
      </td>
    );
  };

  const StatusCell = (props: any) => {
    const { dataItem } = props;
    let textColor = "";

    switch (dataItem.status) {
      case ASSIGNED:
        textColor = "text-[#FF9800]";
        break;
      case OVERDUE:
        textColor = "text-[#D32F2F]";
        break;
      case RELEASE_PENDING:
        textColor = "text-[#219653]";
        break;
      case RELEASED:
        textColor = "text-[#219653]";
        break;
      case ACCEPTED:
        textColor = "text-[#0095ff]";
        break;
      case REJECTED:
        textColor = "text-[#ff3d71]";
        break;
    }

    return (
      <td
        style={{ fontWeight: "700", border: "none", padding: "0" }}
        className={`${textColor} uppercase font-black border-none`}
      >
        {props.dataItem[props.field as string]}
      </td>
    );
  };

  const renderDrawerContent = () => (
    <>
      <TabStrip
        tabPosition={"top"}
        selected={activeTab}
        onSelect={(e) => setActiveTab(e.selected)}
      >
        <TabStripTab
          title={
            <span className="text-txtPrimary inline-flex items-center gap-2">
              <img src="./images/details-icon.png" alt="" />
              Details
            </span>
          }
          contentClassName="w-full"
        >
          <div className="border border-[#E0E5F2] p-4 w-full mt-4">
            <div className="grid grid-cols-2 w-full gap-x-3 gap-y-5">
              <AARTextField
                name="name"
                value={selectedLevel?.name}
                label="Level"
                placeholder=""
                disabled={true}
                required={false}
                onValidate={() => {}}
                onChange={() => {}}
              />
              <AARTextField
                name="name"
                value={selectedArea?.name}
                label="Area"
                placeholder=""
                disabled={true}
                required={false}
                onValidate={() => {}}
                onChange={() => {}}
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <Grid
              data={isDataLoading ? shimmerData : orderBy(gridData, sort)}
              skip={page.skip}
              take={page.take}
              total={isDataLoading ? 5 : total}
              pageable={isDataLoading ? false : { buttonCount: 5, info: true }}
              sortable={isDataLoading ? false : { mode: "single" }}
              onPageChange={pageChange}
              onSortChange={sortChange}
              style={{ height: "500px", width: "100%", maxWidth: "100%" }}
              className="overflow-x-hidden"
              rowRender={(trElement, props) => {
                if (isDataLoading) {
                  const existingClassName = trElement.props.className || "";
                  return React.cloneElement(trElement, {
                    className: `${existingClassName} shimmer-row`.trim(),
                  });
                }
                return trElement;
              }}
            >
              <GridToolbar className="py-1">
                <div className="text-[16px] font-medium text-txtPrimary flex">
                  <ListCheck className="w-5 h-5 mr-2" />
                  <p>AAR Requests</p>
                </div>
              </GridToolbar>
              <GridColumn
                field="refNo"
                title="Reference No."
                cells={{
                  data: ({ dataItem }: { dataItem: any }) => (
                    <td>
                      {isDataLoading ? (
                        <div className="shimmer-cell" />
                      ) : (
                        dataItem.refNo
                      )}
                    </td>
                  ),
                }}
              />
              <GridColumn
                field="from"
                title="From"
                filter="date"
                cells={{
                  data: ({ dataItem }: { dataItem: any }) => (
                    <td>
                      {isDataLoading ? (
                        <div className="shimmer-cell" />
                      ) : (
                        convertDateWithoutTimeString(dataItem.from as string)
                      )}
                    </td>
                  ),
                }}
              />
              <GridColumn
                field="to"
                title="To"
                filter="date"
                cells={{
                  data: ({ dataItem }: { dataItem: any }) => (
                    <td>
                      {isDataLoading ? (
                        <div className="shimmer-cell" />
                      ) : (
                        convertDateWithoutTimeString(dataItem.to as string)
                      )}
                    </td>
                  ),
                }}
              />
              <GridColumn
                field="status"
                title="Status"
                cells={{
                  data: (props: any) => (
                    <td>
                      {isDataLoading ? (
                        <div className="shimmer-cell" style={{ width: '60%' }} />
                      ) : (
                        <StatusCell {...props} />
                      )}
                    </td>
                  ),
                }}
              />
              {/* Company */}
              <GridColumn
                title="Company"
                cells={{
                  data: ({ dataItem }: { dataItem: any }) => (
                    <td>
                      {isDataLoading ? (
                        <div className="shimmer-cell" />
                      ) : (
                        dataItem.companies.map((c: any) => c.name).join(", ")
                      )}
                    </td>
                  ),
                }}
              />

              {/* Activities */}
              <GridColumn
                title="Activities"
                cells={{
                  data: ({ dataItem }: { dataItem: any }) => (
                    <td>
                      {isDataLoading ? (
                        <div className="shimmer-cell" />
                      ) : (
                        dataItem.companies
                          .flatMap((c: any) =>
                            c.activities.map((a: any) => a.name.trim())
                          )
                          .map((act: string, idx: number) => (
                            <div key={idx}>{act}</div>
                          ))
                      )}
                    </td>
                  ),
                }}
              />

              {/* Action */}
              <GridColumn
                title="Action"
                width="120px"
                cells={{
                  data: (props: any) => (
                    <td>
                      {isDataLoading ? (
                        <div className="shimmer-cell" style={{ width: '70px' }} />
                      ) : (
                        <ActionCell {...props} />
                      )}
                    </td>
                  ),
                }}
              />
            </Grid>
          </div>
        </TabStripTab>

        <TabStripTab
          title={
            <span className="text-txtPrimary inline-flex items-center gap-2">
              <img src="./images/activity-logs-icon.png" alt="" />
              Activity Logs
            </span>
          }
        >
          <ActivityLogs
            area={selectedArea}
            level={selectedLevel}
            handlePreview={handlePreview}
            imagePreviewWidth={imagePreviewWidth}
            imagePreviewHeight={imagePreviewHeight}
          />
        </TabStripTab>
      </TabStrip>
    </>
  );

  return (
    <BaseDrawer
      isOpen={showAssignmentsDrawer}
      showIcon={true}
      onClose={() => setShowAssignmentsDrawer(false)}
      title={selectedArea?.name ? `${selectedArea?.name}` : "Area Submission"}
      primaryActionText={"Assign"}
      primaryActionDisabled={false}
      onPrimaryAction={() => {
        setShowAssignmentsDrawer(false);
        setShowSubmissionDrawer(!showSubmissionDrawer);
      }}
      secondaryActionText={
        selectedArea?.status === AVAILABLE || selectedArea?.status === RELEASED
          ? "Cancel"
          : "Close"
      }
      showPrimaryAction={isAuthorize([SUPER_ADMIN, PROJECT_ADMIN, ENGINEER])}
      maxW={"70vw"}
      status={selectedArea?.status}
      {...drawerProps}
    >
      {renderDrawerContent()}
    </BaseDrawer>
  );
}

export default AssignmentsDrawer;
